<template>
  <div>
    <div class="mb-6 tablet:mb-0 tablet:flex tablet:items-center">
      <div class="tablet:w-1/2 tablet:pr-2 desktop:flex-1 desktop:pr-6">
        <h2
          class="text-solution-h2 mb-6 bg-gradient-to-t from-green-400 to-blue-400 bg-clip-text text-transparent tablet:line-clamp-2"
        >
          {{ itemData.title }}
        </h2>
        <h3 class="text-solution-h3 mb-6 tablet:line-clamp-2">
          {{ itemData.subTitle }}
        </h3>
        <p class="mb-6 w-full text-sm text-gray-600 tablet:line-clamp-4">
          {{ itemData.description }}
        </p>
        <div v-if="!isMobile" class="flex flex-wrap">
          <TheButton
            v-if="button01?.text"
            :id="button01?.trackingCode"
            :to="provideFormatUrl(button01.link)"
            :title="button01.text"
            class="mb-3 w-full tablet:mr-2.5 tablet:w-[180px] desktop:mr-6"
          >
            <span class="text-sm font-bold">
              {{ button01.text }}
            </span>
          </TheButton>
          <TheButton
            v-if="button02?.text"
            :id="button02?.trackingCode"
            :to="provideFormatUrl(button02.link)"
            :title="button02.text"
            type="outline"
            class="mb-3 w-full tablet:w-[180px]"
          >
            <span class="text-sm font-bold">
              {{ button02.text }}
            </span>
          </TheButton>
        </div>
      </div>
      <div class="tablet:w-1/2 tablet:pl-2 desktop:w-[380px] desktop:pl-3">
        <LightboxVideo
          v-if="itemData.wistiaCode"
          :video-data="video"
          class="aspect-video w-full rounded"
        />
        <figure
          v-else
          class="relative block aspect-video w-full overflow-hidden rounded"
        >
          <img
            :src="contextThumbnail"
            :alt="itemData.title || itemData.subTitle"
            loading="lazy"
            class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
          />
        </figure>
      </div>
    </div>
    <div v-if="isMobile" class="flex flex-wrap">
      <TheButton
        v-if="button01?.text"
        :id="button01?.trackingCode"
        :to="provideFormatUrl(button01.link)"
        :title="button01.text"
        class="mb-3 w-full"
      >
        <span class="text-sm font-bold">
          {{ button01.text }}
        </span>
      </TheButton>
      <TheButton
        v-if="button02?.text"
        :id="button02?.trackingCode"
        :to="provideFormatUrl(button02.link)"
        :title="button02.text"
        type="outline"
        class="w-full"
      >
        <span class="text-sm font-bold">
          {{ button02.text }}
        </span>
      </TheButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import type {
    IButton,
    ISuiteData,
  } from '@/components/Shared/Suite/Suite/index.vue';
  import type { TUrlConnector } from '@/components/Shared/Suite/type.ts';

  interface IProps {
    itemData: ISuiteData;
    urlConnector?: TUrlConnector;
  }

  const props = defineProps<IProps>();

  const { isMobile } = useScreen();
  const { provideFormatUrl } = useFormatUrl();
  const { provideResponsiveImgUrl } = useSolutionsData();

  const video = computed(
    (): IVideoData => ({
      title: props.itemData.title,
      description: props.itemData.description,
      url: props.itemData.wistiaCode || '',
      imageUrl: contextThumbnail.value,
    })
  );

  const contextThumbnail = computed((): string => {
    const { thumbnail } = props.itemData;
    if (!thumbnail) return '';

    if (props.urlConnector === 'solution') {
      return provideResponsiveImgUrl(thumbnail);
    }
    return thumbnail;
  });

  const button01 = computed((): IButton | undefined => {
    return props.itemData.buttons[0];
  });

  const button02 = computed((): IButton | undefined => {
    return props.itemData.buttons[1];
  });
</script>
